import { recalculateValues } from "./resets/resetDependencies"

class Configuration {
  #params = null
  #store = null
  #mode = "standard"

  /**
   * Class Constructor.
   * @param params
   * @param store
   * @param mode
   */
  constructor(params = null, store = null, mode = "standard") {
    this.#params = params
    this.#store = store
    this.#mode = this.#isValidMode(mode) ? mode : "standard"
  }

  /**
   * Get the initial params of the configuration.
   * @returns {{}|null}
   */
  getParams() {
    return this.#params
  }

  /**
   * Set the initial params of the configuration.
   * @param params
   * @returns {Configuration}
   */
  setParams(params) {
    this.#params = params
    return this
  }

  /**
   * Set the flag for the type of configuration.
   * @param mode
   * @returns {Configuration}
   */
  setMode(mode) {
    this.#mode = this.#isValidMode(mode) ? mode : "standard"
    return this
  }

  /**
   * Set the app store access.
   * @param store
   * @returns {Configuration}
   */
  setStore(store) {
    this.#store = store
    return this
  }

  /**
   * Get the proccessed configuration values.
   * @returns {{}}
   */
  getValues() {
    if (!this.#params || !this.isValid()) {
      return null
    }

    return this.#mode === "standard"
      ? this.#getStandardValues()
      : this.#getCustomValues()
  }

  /**
   * Check if the provided mode is valid
   * @param {string} mode
   * @returns bool
   */
  #isValidMode(mode) {
    return ["standard", "CCO", "CCR"].includes(mode)
  }

  /**
   * Return the custom proccessed configuration values.
   * @returns {{}}
   */
  #getCustomValues() {
    return Object.assign(
      {},
      ...Object.keys(this.#params).map(property => ({
        [property]:
          typeof this.#params[property] === "string" &&
          this.#params[property].includes("NODEF")
            ? ""
            : this.#params[property],
      }))
    )
  }

  /**
   * Return the standard proccessed configuration values.
   * @returns {{}}
   */
  #getStandardValues() {
    // load default values for selected boarding type and accessibility type
    const evModel = this.#params.MODELO

    // load selected preset (predesign) data
    const presetValues = this.#store.predesigns?.[evModel] ?? null

    return recalculateValues(presetValues, presetValues)
  }

  /**
   * Check if the configuration provided is valid.
   * @returns {boolean}
   */
  isValid() {
    return this.#isValidStandard()
  }

  /**
   * Check if the standard configuration is valid.
   * @returns {boolean}
   */
  #isValidStandard() {
    return this.#store?.predesigns.hasOwnProperty(this.#params.MODELO)
  }
}

export default Configuration
